import React from "react"
import { observer } from "mobx-react"
import SVG from "react-inlinesvg"
import capitalize from "lodash/capitalize"

import checkMarkNoSvg from "../../../../priv/static/svg/generic/checkmark-no.svg"
import chevronRightSvg from "../../../../priv/static/svg/generic/chevron-right.svg"
import chevronDownSvg from "../../../../priv/static/svg/generic/chevron-down.svg"
import plusSvg from "../../../../priv/static/svg/generic/plus.svg"
import passengerMaleSvg from "../../../../priv/static/svg/generic/user.svg"
import passengerFemaleSvg from "../../../../priv/static/svg/generic/user.svg"
import briefcaseSvg from "../../../../priv/static/svg/generic/user.svg"
import alertOctagonSvg from "../../../../priv/static/svg/generic/alert-octagon.svg"
import alertCircle from "../../../../priv/static/icons/fill/warning-circle-fill.svg"
import arrowUpSvg from "../../../../priv/static/svg/generic/arrow-up.svg"
import arrowDownSvg from "../../../../priv/static/svg/generic/arrow-down.svg"

import Assignment from "./models/Assignment"
import AssignmentCollection from "./collections/AssignmentCollection"
import InfoTooltip from "../shared/InfoTooltip"
import { pluralize } from "../../util/string"
import { Section, MismatchAlert, MismatchAlertsByPassengerName } from "./models/Section"

interface Props {
  section: Section
  allAssignments: AssignmentCollection
  isExpanded: boolean
  onExpansionToggle: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  sectionNounSingular: string
  sectionNounPlural: string
  isTripLeader: boolean
  tripLeaderPermission: "full" | "assign_only" | "read_only"
}

@observer
export default class SectionView extends React.Component<Props> {
  constructor(props) {
    super(props)
  }

  render() {
    let { section } = this.props
    return (
      <div className="stack border-gray-3 pad-4 gap-2">
        {this.renderSectionSummaryLine()}
        {this.props.isExpanded && this.renderExpandedBody()}
      </div>
    )
  }

  renderSectionSummaryLine() {
    const { section, sectionNounSingular } = this.props
    const assignmentCount = section.assignments().items.length
    const editSectionPath = `${window.location.pathname}/sections/${section.id}/edit`

    return (
      <div
        className="row gap-2 align-start"
        data-role="section-summary-line"
        data-testid={section.id}
        data-expansion-state={this.props.isExpanded ? "expanded" : "minimized"}>
        <a onClick={this.props.onExpansionToggle} data-role="toggle-expand-icon">
          {this.renderExpandIcon()}
        </a>
        <div className="stack grow-1 gap-2">
          <div className="row gap-2">
            <div className="row gap-1 align-baseline" data-role="name">
              <SectionSummaryAlertTooltip section={section} sectionNounSingular={sectionNounSingular} />
              <span className="text-lg weight-700">
                {capitalize(sectionNounSingular)} {section.number}
              </span>
              {section.name && <span className="text-lg">({section.name})</span>}
            </div>
            <div className="row gap-1 grow-1 align-baseline">
              <SectionPreferences section={section} />•
              <a className="text-sm underline" href={editSectionPath} data-role={`edit-section[${section.number}]`}>
                Edit {sectionNounSingular}
              </a>
            </div>
            <div className="text-lg grow-0" data-role="seat-status">
              <SeatStatusBox section={section} />
            </div>
          </div>
          <div className="text-lg">
            {section
              .assignments()
              .items.slice(0, 10)
              .map((assignment, idx) => (
                <span>
                  {assignment.passengerLastName}
                  {idx < 9 && idx < assignmentCount - 1 && " • "}
                </span>
              ))}
            {assignmentCount > 10 && " • ..."}
          </div>
          {section.notes && section.notes !== "" && (
            <Notes section={section} sectionNounSingular={this.props.sectionNounSingular} />
          )}
        </div>

        <div className="stack gap-2 min-inline-12">
          <button
            type="button"
            onClick={() => this.props.onMoveUp()}
            data-role={`move-up[${section.number}]`}
            aria-label="Move up"
            className="pad-inline-0 pad-block-0 tiny bg-transparent hover:bg-transparent fg-gray-6 hover:fg-gray-6">
            <SVG src={arrowUpSvg} className="" />
          </button>
          <button
            type="button"
            onClick={() => this.props.onMoveDown()}
            data-role={`move-down[${section.number}]`}
            aria-label="Move down"
            className="pad-inline-0 pad-block-0 tiny bg-transparent hover:bg-transparent fg-gray-6 hover:fg-gray-6 basis-6">
            <SVG src={arrowDownSvg} />
          </button>
        </div>
      </div>
    )
  }

  renderExpandedBody() {
    const { section, allAssignments, sectionNounSingular, tripLeaderPermission, isTripLeader } = this.props

    return (
      <div className="list-create-holder">
        <AssignedPassengersTable
          tripLeaderPermission={tripLeaderPermission}
          isTripLeader={isTripLeader}
          section={section}
          sectionNounSingular={sectionNounSingular}
        />
        <AvailablePassengersTable
          section={section}
          allAssignments={allAssignments}
          sectionNounSingular={sectionNounSingular}
          tripLeaderPermission={tripLeaderPermission}
          isTripLeader={isTripLeader}
        />
      </div>
    )
  }

  renderExpandIcon = () => {
    let expandButtonCharacter
    if (this.props.isExpanded) {
      expandButtonCharacter = <SVG src={chevronDownSvg} className="inline-block svg-icon-direct" />
    } else {
      expandButtonCharacter = <SVG src={chevronRightSvg} className="inline-block svg-icon-direct" />
    }

    return <div className="inline-block">{expandButtonCharacter}</div>
  }
}

interface SeatStatusBoxProps {
  section: Section
}
const SeatStatusBox = observer(({ section }: SeatStatusBoxProps) => {
  const capacity: number = section.capacity
  const totalAssignedPassengers: number = section.totalAssignedPassengers()

  if (totalAssignedPassengers > capacity || totalAssignedPassengers < capacity) {
    return (
      <div className={`row gap-1 text-red-700 font-bold justify-end`}>
        <SVG src={alertCircle} className="inline-5 aspect-square" /> {totalAssignedPassengers} of {capacity}
      </div>
    )
  } else {
    return (
      <div className={`row gap-1 text-green-700 font-bold justify-end`}>
        {totalAssignedPassengers} of {capacity}
      </div>
    )
  }
})

interface SectionPreferencesProps {
  section: Section
}

const formatShortGender = (genderStr: String) => {
  if (genderStr == "male") {
    return "M"
  } else if (genderStr == "female") {
    return "F"
  } else if (genderStr == "x") {
    return "X"
  }
}

const SectionPreferences = observer(({ section }: SectionPreferencesProps) => {
  let preferredGender
  if (section.preferredGender == "male") {
    preferredGender = "Male"
  } else if (section.preferredGender == "female") {
    preferredGender = "Female"
  } else {
    preferredGender = "Any Gender"
  }

  let preferredPackage
  if (section.preferredPackage != undefined) {
    preferredPackage = section.preferredPackage.name
  } else {
    preferredPackage = "Any Package"
  }

  return (
    <div className="row gap-1 text-sm" data-role="preferences">
      <span>Gender: {preferredGender}</span> • <span>Package: {preferredPackage}</span>
    </div>
  )
})

interface SectionSummaryAlertTooltipProps {
  section: Section
  sectionNounSingular: string
}
const SectionSummaryAlertTooltip = observer(({ section, sectionNounSingular }: SectionSummaryAlertTooltipProps) => {
  const allSectionAlerts: MismatchAlertsByPassengerName | undefined = section.allMismatchAlerts()

  if (allSectionAlerts) {
    return (
      <div data-role="section-mismatch-alert">
        <InfoTooltip
          svgIcon={alertOctagonSvg}
          iconClassName="svg-icon-s pad-block-0.75 text-red-700"
          data-role="section-mismatch-alert">
          <div className="font-normal font-base">
            {Object.entries(allSectionAlerts).map(([passengerName, passengerAlerts], idx) => (
              <div key={idx}>
                <div className="font-bold">{passengerName}</div>
                <ul className="mb-3">
                  {passengerAlerts.map((alert: MismatchAlert, idx) => (
                    <AlertValidationSectionItem key={idx} alert={alert} sectionNounSingular={sectionNounSingular} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </InfoTooltip>
      </div>
    )
  } else {
    return null
  }
})

interface AlertValidationSectionItemProps {
  alert: MismatchAlert
  sectionNounSingular: string
}
const AlertValidationSectionItem = observer(({ alert, sectionNounSingular }: AlertValidationSectionItemProps) => {
  if (alert.field == "gender") {
    return (
      <li>
        Gender <b>{alert.actual}</b> does not match this {sectionNounSingular}'s preferences
      </li>
    )
  } else if (alert.field == "package") {
    return (
      <li>
        Package <b>{alert.actual}</b> does not match this {sectionNounSingular}'s preferences
      </li>
    )
  } else if (alert.field == "cancelled") {
    return <li>Passenger has been cancelled from this trip</li>
  } else if (alert.field == "on_waitlist") {
    return <li>Passenger is on a waitlist</li>
  }
})

interface AssignedPassengersTableProps {
  section: Section
  sectionNounSingular: string
  tripLeaderPermission: "full" | "assign_only" | "read_only"
  isTripLeader: boolean
}
const AssignedPassengersTable = observer(
  ({ section, sectionNounSingular, tripLeaderPermission, isTripLeader }: AssignedPassengersTableProps) => {
    const availableSeats = section.availableSeats()

    let bookedSeatsStatusClass = ""
    if (section.isOverbooked()) {
      bookedSeatsStatusClass = "text-red-700 font-bold"
    }

    return (
      <div data-role="assigned-passengers-table" className="">
        <div className="justify-between gap-5 mb-1 portrait_mobile:flex">
          <div className="font-bold text-base2">Assigned To This {capitalize(sectionNounSingular)}</div>
          <div className="flex-none">
            <div className={bookedSeatsStatusClass} data-role="booked-count">
              {section.totalAssignedPassengers()} of {section.capacity} Assigned
            </div>
          </div>
        </div>

        <div className="scrollable font-gray-700 passenger-rows" data-role="seat-rows">
          {section.assignments().items.map((assignment, idx) => (
            <>
              <AssignedPassengersRow
                key={idx}
                section={section}
                assignment={assignment}
                sectionNounSingular={sectionNounSingular}
                tripLeaderPermission={tripLeaderPermission}
                isTripLeader={isTripLeader}
              />
              <div className="divider" />
            </>
          ))}

          <div className="space pad-inline-2">
            <span>
              <SVG src={briefcaseSvg} className="svg-icon-s" />
              <span className="pad-inline-1">{availableSeats} available spaces</span>
            </span>
          </div>
        </div>
      </div>
    )
  }
)
interface AssignedPassengersRowProps {
  section: Section
  assignment: Assignment
  sectionNounSingular: string
  tripLeaderPermission: "full" | "assign_only" | "read_only"
  isTripLeader: boolean
}
const AssignedPassengersRow = observer(
  ({ assignment, section, sectionNounSingular, isTripLeader, tripLeaderPermission }: AssignedPassengersRowProps) => {
    let passengerIconSvg
    if (assignment.gender == "male") {
      passengerIconSvg = passengerMaleSvg
    } else {
      passengerIconSvg = passengerFemaleSvg
    }

    const removeFromListLink = () => {
      if (isTripLeader && tripLeaderPermission === "read_only") {
        return ""
      } else {
        return (
          <button
            type="button"
            aria-label="Remove from section"
            className="pad-inline-0 pad-block-0 small basis-9 shrink-0 bg-transparent hover:bg-transparent border-black fg-black hover:fg-black remove-x svg-icon"
            onClick={() => assignment.unassign()}
            data-role="remove-from-section-link"
            data-participation-id={assignment.participation.id}>
            <SVG src={checkMarkNoSvg} className="" />
          </button>
        )
      }
    }

    const sectionAssignmentAlerts = section.mismatchAlerts(assignment)
    let maybeAlertTooltip
    if (sectionAssignmentAlerts.length >= 1) {
      maybeAlertTooltip = (
        <InfoTooltip svgIcon={alertOctagonSvg} iconClassName="text-red-700 svg-icon-s font-base">
          <ul className="p-4 font-base">
            {sectionAssignmentAlerts.map((alert: MismatchAlert, idx) => (
              <AlertValidationSectionItem key={idx} alert={alert} sectionNounSingular={sectionNounSingular} />
            ))}
          </ul>
        </InfoTooltip>
      )
    }

    return (
      <div className="row gap-2 pad-block-1 pad-inline-2">
        <SVG src={passengerIconSvg} className={assignment.gender} />
        <div className="stack gap-0.5 grow-1">
          <div className="row gap-0.5">
            <span className="text-lg weight-600">{assignment.passengerName}</span>
            <span className="inline-1" />({formatShortGender(assignment.gender)}, Age {assignment.passengerAge})
            {maybeAlertTooltip}
          </div>
          <div>{assignment.package ? <span>Package: {assignment.package.name}</span> : <span>No package</span>}</div>
        </div>
        {removeFromListLink()}
      </div>
    )
  }
)

interface AvailablePassengersTableProps {
  section: Section
  allAssignments: AssignmentCollection
  sectionNounSingular: string
  tripLeaderPermission: "full" | "assign_only" | "read_only"
  isTripLeader: boolean
}

interface AvailablePassengersTableState {
  currentSearchString: string
  showOnlyPassengersMatchingSectionPreferences: boolean
}

@observer
class AvailablePassengersTable extends React.Component<AvailablePassengersTableProps, AvailablePassengersTableState> {
  constructor(props) {
    super(props)
    this.state = { currentSearchString: "", showOnlyPassengersMatchingSectionPreferences: true }
  }

  changeSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ currentSearchString: e.target.value })
  }

  render() {
    const { section, allAssignments, sectionNounSingular, isTripLeader, tripLeaderPermission } = this.props
    const { showOnlyPassengersMatchingSectionPreferences, currentSearchString } = this.state

    const unassignedAssignments = allAssignments
      .availableForSection(section, showOnlyPassengersMatchingSectionPreferences)
      .search(currentSearchString)

    return (
      <div className="stack gap-2 select-none" data-role="available-passengers-table">
        <div className="mt-5 md:mt-0">
          <div className="font-bold text-base2">Unassigned Passengers</div>
          <div className="items-center justify-between gap-inline-2 pt-2 space-y-2 lg:flex">
            <input
              type="search"
              className="lg:w-7/12 search-bar__input"
              placeholder="Search Unassigned Passengers..."
              value={this.state.currentSearchString}
              onChange={this.changeSearchString}
            />

            <label className="flex items-center gap-inline-2 text-sm heckbox-list place-self-end">
              <input
                type="checkbox"
                checked={!this.state.showOnlyPassengersMatchingSectionPreferences}
                onChange={(e) =>
                  this.setState({
                    showOnlyPassengersMatchingSectionPreferences: !e.target.checked,
                  })
                }
                data-role="show-only-passengers-matching-section-preferences-checkbox"
              />
              Show All Passengers
            </label>
          </div>
        </div>

        <div className="py-5 sm:py-0 scrollable font-gray-700 passenger-rows" data-role="passenger-rows">
          {unassignedAssignments.items.length > 0 ? (
            this.renderUnassignedPassengersTableBody(unassignedAssignments, tripLeaderPermission, isTripLeader)
          ) : (
            <div className="col-span-3 p-2 bg-gray-100 border-gray-200 sm:text-center">
              No unassigned passengers match your query.
            </div>
          )}
        </div>
      </div>
    )
  }

  assignPassengerLink(assignment, section, tripLeaderPermission, isTripLeader) {
    if (isTripLeader && tripLeaderPermission === "read_only") {
      return ""
    } else {
      return (
        <button
          type="button"
          aria-label="Assign to section"
          className="pad-inline-0 pad-block-0 small basis-9 shrink-0 bg-transparent hover:bg-transparent border-black fg-black hover:fg-black remove-x svg-icon"
          onClick={() => assignment.assignToSection(section)}
          data-role="assign-to-section-link"
          data-participation-id={assignment.participation.id}>
          <SVG src={plusSvg} className="" />
        </button>
      )
    }
  }

  renderUnassignedPassengersTableBody(
    unassignedAssignments: AssignmentCollection,
    tripLeaderPermission: "full" | "assign_only" | "read_only",
    isTripLeader: boolean
  ) {
    const { section } = this.props

    return unassignedAssignments.items.map((assignment: Assignment, idx: number) => {
      let passengerIconSvg
      if (assignment.gender == "male") {
        passengerIconSvg = passengerMaleSvg
      } else {
        passengerIconSvg = passengerFemaleSvg
      }
      return (
        <>
          <div className="row gap-2 pad-block-1 pad-inline-2">
            <SVG src={passengerIconSvg} className={assignment.gender} />
            <div className="stack gap-0.5 grow-1">
              <div className="row gap-0.5">
                <span className="text-lg weight-600">{assignment.passengerName}</span>
                <span className="inline-1" />
                {formatShortGender(assignment.gender)}, Age {assignment.passengerAge}
              </div>
              <div>{assignment.package ? <span>Package: {assignment.package.name}</span> : <span>No package</span>}</div>
            </div>
            {this.assignPassengerLink(assignment, section, tripLeaderPermission, isTripLeader)}
          </div>
          <div className="divider" />
        </>
      )
    })
  }
}

interface NotesProps {
  section: Section
  sectionNounSingular: string
}

const Notes = observer(({ section }: NotesProps) => {
  return (
    <div className="bg-orange-50 pad-2" data-role="notes-text">
      <div className="preserve-breaks">
        <strong>Notes:</strong> <span>{section.notes}</span>
      </div>
    </div>
  )
})
